import { filter, BehaviorSubject, combineLatest, Observable } from 'rxjs';

/**
 * @module EventService.EventDateService
 */
function createEventDateService(
    apiService,
    deviceService,
    eventService,
    eventFilter,
    logService,
    util,
    _window
) {
    const _eventDateService = {};
    const eventDates = [];
    const lookup = {};
    const eventDatesSubject = new BehaviorSubject(eventDates);
    let dataSubscription;

    function log(params) {
        params.module = 'eventDateService';
        logService.log(params);
        return params;
    }

    function clear() {
        if (dataSubscription) {
            dataSubscription.unsubscribe();
            dataSubscription = undefined;
        }
        eventDates.splice(0);
        util.clear(lookup);
        eventDatesSubject.next(eventDates);
    }

    function dateOnly(date) {
        return new _window.Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    function earliestToLatest(a, b) {
        if (a.dateValue < b.dateValue) {
            return -1;
        }
        if (a.dateValue > b.dateValue) {
            return 1;
        }
        return 0;
    }

    function getEventDate(dateValue) {
        let eventDate = lookup[dateValue];
        if (!eventDate) {
            eventDate = {
                // Count of loaded events with no filters applied.
                // This is the most accurate when fullyLoaded is true.
                count: 0,
                // Count of events from event count server API.
                // May include some events that are never active.
                // No filters are considered.
                apiCount: 0,
                // Count of loaded events with filters applied.
                // This is the most accurate when fullyLoaded is true.
                activeCount: 0,
                dateValue: dateValue,
                date: new _window.Date(dateValue),
                fullyLoaded: false,
                hours: [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 ],
                hoursMax: 0
            };
            eventDates.push(eventDate);
            eventDates.sort(earliestToLatest);
            lookup[dateValue] = eventDate;
        }
        return eventDate;
    }

    /**
     * @description calculate eventDate values based on event info
     * @param {Array<Event>} eventInfo List of events
     */
    function calculateFromEventInfo(eventInfo) {
        if (eventInfo) {
            eventInfo.all.forEach(event => {
                let eventDate = getEventDate(event.dateValue);
                if (eventFilter.internalFilter(event)) {
                    eventDate.count += 1;
                }
            });
            eventInfo.actives.forEach(event => {
                let eventDate = getEventDate(event.dateValue);
                if (eventFilter.isActive(event)) {
                    eventDate.activeCount += 1;
                    eventDate.hours[event.date.getHours()] += 1;
                }
            });
        }
    }

    /**
     * @description calculates eventDate summary from event count data
     * @param {Object} eventCountInfo Data from the event count API
     */
    function calculateFromEventCountInfo(eventCountInfo) {
        if (eventCountInfo) {
            Object.keys(eventCountInfo).forEach(srcId => {
                let key = 'apiCount-' + srcId;
                eventCountInfo[srcId].forEach(function(eventCountRecord) {
                    const date = dateOnly(new _window.Date(eventCountRecord.dttm));
                    const dateValue = date.getTime();
                    const eventDate = getEventDate(dateValue);
                    // overall apiCount for date - will include user events
                    eventDate.apiCount += eventCountRecord.count;
                    // apiCount per srcId (deviceId or userId)
                    if (eventDate[key] === undefined) {
                        eventDate[key] = 0;
                    }
                    eventDate[key] += eventCountRecord.count;
                });
            });
        }
    }

    /**
     * @description calculates eventDate summary info such as hoursMax and fully loaded
     * @param {Object} data A collection of data related to event counts
     * @param {Array<Device>} data.devices
     * @param {Object} data.filterInfo Filter information
     * @param {Object} data.filterInfo.filterState The currently active filter conditions
     * @param {Array<Event>} data.eventInfo List of events
     * @param {Object} data.eventCountInfo Data from the event count API
     * @param {Array<EventDate>} eventDates array of eventDate Objects
     */
    function calculateSummaryData(data, eventDates) {
        eventDates.forEach(eventDate => {
            eventDate.hoursMax = Math.max.apply(null, eventDate.hours);
            const tomorrow = util.getTomorrowDate(eventDate.dateValue).getTime();
            if (data?.filterInfo?.filterState) {
                let deviceIds = data.filterInfo.filterState.devices;
                if (!deviceIds && data.devices) {
                    deviceIds = data.devices.map(d => d.id);
                }
                if (deviceIds) {
                    eventDate.fullyLoaded = deviceIds.length > 0 && deviceIds.every(srcId =>
                        eventService.hasAllEventsForDateRange({
                            group: srcId,
                            startTime: eventDate.dateValue,
                            endTime: tomorrow
                        })
                    );
                } else {
                    eventDate.fullyLoaded = false;
                }
            }
        });
    }

    /**
     * @description master function for calculating all eventDate values
     * @param {Object} data A collection of data related to event counts
     * @param {Array<Device>} data.devices
     * @param {Object} data.filterInfo Filter information
     * @param {Object} data.filterInfo.filterState The currently active filter conditions
     * @param {Array<Event>} data.eventInfo List of events
     * @param {Object} data.eventCountInfo Data from the event count API
     */
    function calculateEventDates(data) {
        // clear any previous data
        eventDates.forEach(eventDate => {
            eventDate.count = 0;
            eventDate.apiCount = 0;
            eventDate.activeCount = 0;
            eventDate.hours.fill(0);
            Object.keys(eventDate).forEach(key => {
                if (key.startsWith('apiCount-') ) {
                    delete eventDate[key];
                }
            });
        });
        calculateFromEventInfo(data.eventInfo);
        calculateFromEventCountInfo(data.eventCountInfo);
        // remove any eventDates that have no events
        let i = 0;
        while (i < eventDates.length) {
            if (eventDates[i].apiCount === 0 && eventDates[i].count === 0) {
                delete lookup[eventDates[i].dateValue];
                eventDates.splice(i, 1);
            } else {
                i += 1;
            }
        }
        calculateSummaryData(data, eventDates);
        eventDatesSubject.next(eventDates);
    }

    function getCount(params) {
        return new Observable(subscriber => {
            apiService.events.count(params).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getCountFailed',
                        traceId: e?.traceId
                    })
                )
            );
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @description Emits an Array of eventDate that contains information about event counts
     * @returns {Observable<Array<EventDate>>}
     * @name get
     * @method
     */
    // TODO: add a refresh param to update getCount()
    _eventDateService.get = function(params) {
        dataSubscription ??= combineLatest({
            devices: deviceService.get({
                context: deviceService.contextEnum.forEvents,
                traceId: params?.traceId
            }),
            filterInfo: eventService.getFilter(),
            eventInfo: eventService.get(),
            eventCountInfo: getCount(params)
        }).subscribe((result) => calculateEventDates(result));
        return eventDatesSubject.asObservable();
    };

    _eventDateService.getForDateValue = function(dateValue) {
        return getEventDate(dateValue);
    };

    /**
     * @param {Number} dateValue a number representing a date without the time info
     * @returns {EventDate} The next EventDate after the given dateValue, if it exists
     * @name getPrevious
     * @method
     */
    _eventDateService.getPrevious = function(dateValue) {
        let index = eventDates.indexOf(lookup[dateValue]);
        if (index > 0) {
            return eventDates[index - 1];
        }
    };

    /**
     * @param {Number} dateValue a number representing a date without the time info
     * @returns {EventDate} The previous EventDate before the given dateValue, if it exists
     * @name getNext
     * @method
     */
    _eventDateService.getNext = function(dateValue) {
        let index = eventDates.indexOf(lookup[dateValue]);
        if (index > -1 && index < eventDates.length - 1) {
            return eventDates[index + 1];
        }
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_eventDateService);
}

export default createEventDateService;
